/**
 * features/notifications/components/NotificationBell.tsx
 *
 * Campana global del shell (N1): contador de no leídas + panel/feed con prioridad, título, cuerpo,
 * módulo humano y fecha relativa. Marca leída (individual) y todas. Estados claros de vacío/carga/error.
 * Mobile-first: el panel nunca desborda (ancho acotado a la ventana, alto con scroll propio).
 */

import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@shared/utils/cn'
import {
  useNotifications,
  type NotificationPriority,
  type NotificationItem,
} from '../hooks/useNotifications'
import { resolveEntityRoute } from '../entityRoute'

const MODULE_LABEL: Record<string, string> = {
  'platform.notifications': 'Sistema',
  platform: 'Sistema',
  repairs: 'Reparaciones',
  warranties: 'Garantías',
  'commerce.credit': 'Crédito',
  'commerce.layaway': 'Apartados',
  'inventory.transfers': 'Traspasos',
  'procurement.orders': 'Compras',
}
const moduleLabel = (m: string) => MODULE_LABEL[m] ?? m

const PRIORITY_META: Record<NotificationPriority, { label: string; tone: string }> = {
  INFO: { label: 'Info', tone: 'bg-brand-100 text-brand-700' },
  ACTION_REQUIRED: { label: 'Acción', tone: 'bg-warning-100 text-warning-700' },
  URGENT: { label: 'Urgente', tone: 'bg-danger-100 text-danger-700' },
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'ahora'
  if (min < 60) return `hace ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.floor(h / 24)
  if (d < 7) return `hace ${d} d`
  return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const { items, unread, loading, error, markRead, markAll } = useNotifications()
  const navigate = useNavigate()
  // Candado por-id: impide dobles acciones accidentales sobre la MISMA notificación
  // (un doble-click no dispara markRead/navegación dos veces); otras sí se pueden pulsar.
  const actingIdRef = useRef<string | null>(null)

  /**
   * Al pulsar una notificación: marcarla leída (best-effort) y, si su entidad está
   * mapeada, abrirla. La navegación NO se bloquea si falla el marcado. Entidad
   * desconocida o sin id: sólo marca leída, sin navegar y sin romper la UI. Una
   * notificación ya leída también puede abrir su entidad (no se vuelve a marcar).
   */
  const handleActivate = (n: NotificationItem) => {
    if (actingIdRef.current === n.id) return
    actingIdRef.current = n.id

    if (n.read_at == null) {
      // best-effort: si falla, no rompe ni bloquea la navegación legítima.
      void markRead(n.id).catch(() => {})
    }

    const route = resolveEntityRoute(n.entity_type, n.entity_id)
    if (route) {
      setOpen(false)
      navigate(route)
    }

    window.setTimeout(() => {
      if (actingIdRef.current === n.id) actingIdRef.current = null
    }, 300)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        aria-label={`Notificaciones${unread > 0 ? ` (${unread} sin leer)` : ''}`}
        className="relative flex items-center justify-center w-8 h-8 rounded-lg text-surface-400 hover:text-white hover:bg-navy-700 transition-colors"
      >
        <span className="text-base leading-none">🔔</span>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-danger-500 text-white text-[10px] font-black flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop para cerrar al tocar fuera */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 mt-2 w-[min(92vw,360px)] max-h-[70vh] flex flex-col bg-white rounded-2xl shadow-xl border border-surface-100 z-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-surface-100 flex-shrink-0">
              <p className="text-[13px] font-black text-navy-900">Notificaciones</p>
              {unread > 0 && (
                <button
                  onClick={() => void markAll()}
                  className="text-[11px] font-semibold text-brand-600 hover:text-brand-700"
                >
                  Marcar todas
                </button>
              )}
            </div>

            <div className="overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-3">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="h-10 bg-surface-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : error ? (
                <div className="p-6 text-center text-[12px] text-navy-400">{error}</div>
              ) : items.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-2xl mb-2">🌙</p>
                  <p className="text-[13px] text-navy-500">Sin notificaciones pendientes</p>
                </div>
              ) : (
                <ul className="divide-y divide-surface-50">
                  {items.map(n => {
                    const meta = PRIORITY_META[n.priority] ?? PRIORITY_META.INFO
                    const isUnread = n.read_at == null
                    return (
                      <li key={n.id}>
                        <button
                          onClick={() => handleActivate(n)}
                          className={cn(
                            'w-full text-left px-4 py-3 flex gap-2.5 hover:bg-surface-50 transition-colors',
                            isUnread && 'bg-brand-50/40'
                          )}
                        >
                          <span
                            className={cn(
                              'mt-1 w-2 h-2 rounded-full flex-shrink-0',
                              isUnread ? 'bg-brand-500' : 'bg-transparent'
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span
                                className={cn(
                                  'text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded',
                                  meta.tone
                                )}
                              >
                                {meta.label}
                              </span>
                              <span className="text-[10px] text-navy-400 truncate">
                                {moduleLabel(n.source_module)}
                              </span>
                              <span className="text-[10px] text-navy-300 ml-auto flex-shrink-0">
                                {relTime(n.created_at)}
                              </span>
                            </div>
                            <p
                              className={cn(
                                'text-[13px] leading-snug',
                                isUnread ? 'font-bold text-navy-900' : 'font-medium text-navy-600'
                              )}
                            >
                              {n.title}
                            </p>
                            {n.body && (
                              <p className="text-[12px] text-navy-500 leading-snug mt-0.5">
                                {n.body}
                              </p>
                            )}
                          </div>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
